import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import PhotoGallery from '../components/PhotoGallery';
import StatusManagement from '../components/StatusManagement';
import {
  ArrowLeft,
  UserCircle,
  Phone,
  Prohibit,
  Trash,
  Image as ImageIcon,
  Video,
  PencilSimple,
  Copy,
  Check,
  CalendarBlank,
  ChartBar,
  ChatCircleDots,
  ChatCircleText,
  CaretRight,
  Bank,
  Gear
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { maskPhone, canViewFullPhone } from '../utils/phoneMask';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const isSyntheticSupportPhone = (phone) => typeof phone === 'string' && phone.startsWith('GUEST_SUPPORT_');

const HandlerProfile = ({ customerId: propCustomerId, roomId: propRoomId, onClose, isEmbedded = false }) => {
  const params = useParams();
  const effectiveCustomerId = propCustomerId || params.customerId;
  const navigate = useNavigate();
  const { user, permissions } = useAuth();
  const hasFullPhoneAccess = canViewFullPhone(user);
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);
  const [showEditLogin, setShowEditLogin] = useState(false);
  const [editLoginData, setEditLoginData] = useState({ phone: '', password: '' });
  const [copiedSection, setCopiedSection] = useState(null);
  const [settingsLoginLink, setSettingsLoginLink] = useState('');
  const [bankDetails, setBankDetails] = useState(null);
  const [clientBankDetails, setClientBankDetails] = useState(null);

  useEffect(() => {
    // Reset state immediately on customerId change to prevent cross-user state leaks
    setBankDetails(null);
    setClientBankDetails(null);
    setProfile(null);
    if (!effectiveCustomerId) return;

    const loadProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const storedUser = localStorage.getItem('dws_user');
        const token = storedUser ? JSON.parse(storedUser)?.token : null;
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const searchParams = new URLSearchParams(window.location.search);
        const fromRoom = propRoomId || searchParams.get('from_room');
        const queryStr = fromRoom ? `?from_room=${fromRoom}` : (window.location.search || '');

        const response = await axios.get(`${API}/customers/profile/${effectiveCustomerId}${queryStr}`, { headers });
        const prof = response.data || {};
        setProfile(prof);

        // Fetch dual bank details from client-bank-details endpoint
        try {
          const clientBankRes = await axios.get(`${API}/client-bank-details/${effectiveCustomerId}`, { headers });
          const data = clientBankRes.data || {};
          if (data.saved_bank_detail) setBankDetails(data.saved_bank_detail);
          if (data.client_bank_detail) setClientBankDetails(data.client_bank_detail);
        } catch (e) {}
      } catch (err) {
        console.error('Failed to load profile:', err);
        setError(err?.response?.data?.error || err?.response?.data?.detail || err.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [effectiveCustomerId, propRoomId]);

  // Fetch login link from settings
  useEffect(() => {
    const fetchLoginLink = async () => {
      try {
        const storedUser = localStorage.getItem('dws_user');
        const token = storedUser ? JSON.parse(storedUser)?.token : null;
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/settings`, { headers });
        if (res.data?.customer_login_link) {
          setSettingsLoginLink(res.data.customer_login_link);
        }
      } catch (e) { /* ignore */ }
    };
    fetchLoginLink();
  }, []);

  const handleBlock = async () => {
    try {
      await axios.post(`${API}/clients/block`, {
        customer_id: customerId,
        blocked_by: user?.id,
        blocked_by_name: user?.username,
        blocked_by_role: user?.role,
        reason: "Blocked by " + (user?.role || 'handler')
      });
      setShowBlockConfirm(false);
      toast.success('Customer blocked successfully');
      // Navigate based on user role
      const dashboardRoute = user?.role === 'super_admin' ? '/admin-dashboard' : '/handler-dashboard';
      navigate(dashboardRoute);
    } catch (error) {
      console.error('Failed to block customer:', error);
      toast.error('Failed to block customer');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/customers/${customerId}`);
      setShowDeleteConfirm(false);
      alert('Customer deleted successfully');
      // Navigate based on user role
      const dashboardRoute = user.role === 'super_admin' ? '/admin-dashboard' : '/handler-dashboard';
      navigate(dashboardRoute);
    } catch (error) {
      console.error('Failed to delete customer:', error);
      alert('Failed to delete customer');
    }
  };

  // Edit Login: save updated credentials
  const handleSaveLogin = async () => {
    try {
      const updatePayload = {};
      if (editLoginData.phone) updatePayload.phone = editLoginData.phone;
      if (editLoginData.password) updatePayload.password = editLoginData.password;
      
      await axios.put(`${API}/clients/update-login/${effectiveCustomerId}`, updatePayload);
      toast.success('Login details updated!');
      setShowEditLogin(false);
      // Reload profile
      const searchParams = new URLSearchParams(window.location.search);
      const fromRoom = propRoomId || searchParams.get('from_room');
      const queryStr = fromRoom ? `?from_room=${fromRoom}` : (window.location.search || '');
      const response = await axios.get(`${API}/customers/profile/${effectiveCustomerId}${queryStr}`);
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to update login');
    }
  };

  // Reset to default password: Abcd1234
  const handleResetPassword = async () => {
    try {
      await axios.put(`${API}/clients/update-login/${effectiveCustomerId}`, {
        phone: profile?.customer?.phone || '',
        password: 'Abcd1234'
      });
      setEditLoginData(prev => ({ ...prev, password: 'Abcd1234' }));
      toast.success('Password reset to Abcd1234');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  // Copy login details
  const copyLoginDetails = async () => {
    const c = profile?.customer;
    if (!c) return;
    const loginLink = settingsLoginLink || `${window.location.origin}/customer-login`;
    const text = `Username.. ${c.username || 'N/A'}
DWS Login ID: ${maskPhone(c.phone, hasFullPhoneAccess) || 'N/A'}
DWS Login Password: ${c.password || editLoginData.password || 'N/A'}
Login Link: ${loginLink}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection('login');
      toast.success('Login details copied!');
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Copy bank details
  const copyBankDetails = async () => {
    try {
      const response = await axios.get(`${API}/clients/registered`);
      const client = response.data.find(c => c.id === customerId);
      if (!client) {
        toast.error('Client bank details not found');
        return;
      }
      let text = `User Name: ${client.username || 'N/A'}

A/c Holder Name: ${client.account_holder_name || client.client_name || 'N/A'}

Bank Account No: ${client.bank_account_number || 'N/A'}

IFSC Code: ${client.ifsc_code || 'N/A'}

Bank Name: ${client.bank_name || 'N/A'}`;
      if (client.address) text += `\n\nAddress: ${client.address}`;
      if (client.upi_id) text += `\n\nUPI ID: ${client.upi_id}`;
      
      await navigator.clipboard.writeText(text);
      setCopiedSection('bank');
      toast.success('Bank details copied!');
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      toast.error('Failed to copy bank details');
    }
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const fromRoom = params.get('from_room');
    if (fromRoom) {
      const dashboardRoute = user?.role === 'super_admin' ? '/admin-dashboard' : '/handler-dashboard';
      navigate(dashboardRoute, { state: { selectedChatId: fromRoom } });
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className={`${isEmbedded ? 'w-full h-full' : 'h-screen'} flex flex-col items-center justify-center bg-[#f4f7f6] dark:bg-[#0b141a] p-6`}>
        <div className="w-9 h-9 border-4 border-[#008069] border-t-transparent rounded-full animate-spin mb-3"></div>
        <div className="text-sm font-semibold text-slate-600 dark:text-slate-300 animate-pulse">Loading customer profile...</div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className={`${isEmbedded ? 'w-full h-full' : 'h-screen'} flex flex-col items-center justify-center bg-[#f4f7f6] dark:bg-[#0b141a] p-4 text-center`}>
        <div className="bg-red-500/10 text-red-600 p-4 rounded-xl max-w-md w-full mb-4 border border-red-500/20 shadow-sm">
          <p className="font-semibold text-base mb-1">Failed to load profile</p>
          <p className="text-xs text-red-500">{error || 'Customer profile not found'}</p>
        </div>
        <button
          onClick={handleBack}
          className="px-5 py-2.5 bg-[#008069] text-white rounded-xl text-sm font-semibold hover:bg-[#006e5a] transition-colors shadow-sm cursor-pointer"
        >
          Go Back
        </button>
      </div>
    );
  }

  const { customer, media = [], total_messages, chat_rooms = [] } = profile;

  return (
    <div className={`${isEmbedded ? 'w-full h-full animate-in slide-in-from-right duration-200' : 'h-screen'} flex flex-col bg-[#f4f7f6] dark:bg-[#0b141a] overflow-y-auto overflow-x-hidden`} style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* Top Header */}
      <div className="bg-[#008069] text-white px-4 py-3 flex items-center justify-between shadow-md flex-shrink-0 sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="p-1.5 hover:bg-white/10 rounded-full transition-colors cursor-pointer"
            title="Close Contact Info"
          >
            <ArrowLeft size={20} weight="bold" />
          </button>
          <h1 className="text-base sm:text-lg font-bold tracking-wide">Contact Info</h1>
        </div>
      </div>

      {/* Main Container */}
      <div className={`flex-1 ${isEmbedded ? 'p-3 sm:p-4 space-y-4' : 'p-4 sm:p-6 space-y-5 max-w-6xl mx-auto'} w-full pb-12`}>

        {/* 1. Top Customer Banner Card */}
        <div className="bg-[#e8f8f5] dark:bg-[#112420] border border-[#a2e9db] dark:border-[#1e4d43] rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col items-center text-center gap-3">
          <div className="flex flex-col items-center text-center gap-2">
            <div 
              className="w-20 h-20 sm:w-22 sm:h-22 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm border-2 border-white dark:border-slate-800"
              style={customer.profile_pic ? {} : { backgroundColor: '#a2e9db' }}
            >
              {customer.profile_pic ? (
                <img 
                  src={customer.profile_pic.startsWith('http') ? customer.profile_pic : `${API}${customer.profile_pic}`} 
                  alt={customer.username} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-3xl font-black text-[#0f5449]">
                  {(customer.username || 'C').charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-extrabold text-slate-800 dark:text-slate-100 break-all">{customer.username}</h2>
              {!isSyntheticSupportPhone(customer.phone) && (
                <div className="flex items-center justify-center gap-1.5 text-slate-500 dark:text-slate-400 text-xs sm:text-sm mt-1">
                  <Phone size={15} />
                  <span>{maskPhone(customer.phone, hasFullPhoneAccess)}</span>
                </div>
              )}
              <div className="flex items-center justify-center gap-1.5 text-slate-500 dark:text-slate-400 text-xs mt-1">
                <CalendarBlank size={15} />
                <span>Joined: {customer.created_at ? new Date(customer.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '1 Aug 2026'}</span>
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                {!customer.is_blocked ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 rounded-full text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    Active Customer
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-red-100 dark:bg-red-950/80 text-red-700 dark:text-red-300 rounded-full text-xs font-semibold">
                    Blocked Customer
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons: Edit & Copy */}
          {user?.role !== 'calling_handler' && (
            <div className="flex items-center justify-center gap-3 mt-1 w-full">
              <button
                onClick={() => {
                  setEditLoginData({ phone: customer.phone || '', password: '' });
                  setShowEditLogin(true);
                }}
                className="flex items-center justify-center gap-1.5 px-4 py-2 bg-white dark:bg-[#1a2e2b] border border-slate-200 dark:border-slate-700 hover:bg-slate-50 rounded-xl transition-all shadow-sm text-slate-700 dark:text-slate-200 text-xs font-semibold cursor-pointer"
                title="Edit Login"
              >
                <PencilSimple size={16} className="text-emerald-600" />
                <span>Edit Login</span>
              </button>

              <button
                onClick={copyLoginDetails}
                className="flex items-center justify-center gap-1.5 px-4 py-2 bg-[#f3e8ff] dark:bg-[#2b1b3d] border border-purple-200 dark:border-purple-800 hover:bg-purple-100 rounded-xl transition-all shadow-sm text-purple-700 dark:text-purple-300 text-xs font-semibold cursor-pointer"
                title="Copy Login Info"
              >
                <Copy size={16} />
                <span>{copiedSection === 'login' ? 'Copied!' : 'Copy Info'}</span>
              </button>
            </div>
          )}
        </div>

        {/* 2. Statistics Card */}
        <div className="bg-white dark:bg-[#111b21] rounded-2xl p-4 sm:p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 rounded-xl">
                <ChartBar size={20} weight="bold" />
              </div>
              <div>
                <h3 className="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-100">Statistics</h3>
                <p className="text-[11px] sm:text-xs text-slate-400">Overview of customer activity</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {/* Messages */}
            <div className="bg-[#e8f8f5] dark:bg-[#0e2722] border border-[#a2e9db]/50 dark:border-[#194b41] rounded-xl p-2.5 sm:p-3 flex flex-col items-center text-center justify-center hover:shadow-md transition-all">
              <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-sm mb-1">
                <ChatCircleDots size={16} weight="fill" />
              </div>
              <h4 className="text-lg sm:text-xl font-black text-slate-800 dark:text-slate-100">{total_messages}</h4>
              <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 font-medium">Messages</p>
            </div>

            {/* Media */}
            <div className="bg-[#e0f2fe] dark:bg-[#0c2738] border border-[#bae6fd]/50 dark:border-[#174868] rounded-xl p-2.5 sm:p-3 flex flex-col items-center text-center justify-center hover:shadow-md transition-all">
              <div className="w-8 h-8 rounded-full bg-sky-500 text-white flex items-center justify-center shadow-sm mb-1">
                <ImageIcon size={16} weight="fill" />
              </div>
              <h4 className="text-lg sm:text-xl font-black text-slate-800 dark:text-slate-100">{media.length}</h4>
              <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 font-medium">Media</p>
            </div>

            {/* Chats */}
            <div className="bg-[#f3e8ff] dark:bg-[#251838] border border-[#e9d5ff]/50 dark:border-[#4c2d73] rounded-xl p-2.5 sm:p-3 flex flex-col items-center text-center justify-center hover:shadow-md transition-all">
              <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center shadow-sm mb-1">
                <ChatCircleText size={16} weight="fill" />
              </div>
              <h4 className="text-lg sm:text-xl font-black text-slate-800 dark:text-slate-100">{chat_rooms.length}</h4>
              <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 font-medium">Chats</p>
            </div>
          </div>
        </div>

        {/* 3. IP Origin Timeline Card (Dynamic IP & Count) */}
        <div className="bg-white dark:bg-[#111b21] rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-950/60 text-red-600 flex items-center justify-center font-bold text-xs">
                IP
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">IP Origin Timeline</h3>
                  <span className="bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full">
                    {profile.ip_timeline?.count || 1} IDS
                  </span>
                </div>
                <p className="text-xs text-slate-400">
                  Accounts created from {profile.ip_timeline?.ip || customer.ip_address || '45.119.31.62'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Media Card */}
        <div className="bg-white dark:bg-[#111b21] rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm">
          <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
            <ImageIcon size={20} className="text-slate-500" />
            Media ({media.length})
          </h3>
          {media.length > 0 ? (
            <div className="flex flex-wrap gap-3">
              {media.slice(0, 8).map((item, idx) => (
                <div 
                  key={idx}
                  className="w-16 h-16 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 cursor-pointer hover:opacity-90 hover:scale-105 transition-all shadow-sm"
                  onClick={() => {
                    if (item.image_url) {
                      setGalleryStartIndex(idx);
                      setShowGallery(true);
                    }
                  }}
                >
                  <img 
                    src={item.image_url.startsWith('http') ? item.image_url : `${API}${item.image_url}`} 
                    alt="Media preview" 
                    className="w-full h-full object-cover" 
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400">No media shared yet</p>
          )}
        </div>

        {/* 5. Bank Details Grid */}
        <div className={`grid ${isEmbedded ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-4`}>
          {/* Saved Bank Detail */}
          <div className="bg-white dark:bg-[#111b21] rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
                  <Bank size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Saved Bank Detail</h3>
                  <p className="text-[11px] text-slate-400">Bank account details saved by customer</p>
                </div>
              </div>
              {user?.role !== 'calling_handler' && (
                <button onClick={copyBankDetails} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-slate-500 transition-colors" title="Copy">
                  <Copy size={16} />
                </button>
              )}
            </div>

            {bankDetails && (bankDetails.bank_name || bankDetails.bank_account_number || bankDetails.ifsc_code) ? (
              <div className="bg-slate-50 dark:bg-slate-800/40 rounded-xl p-3.5 space-y-2.5 text-xs">
                <div className="flex justify-between border-b border-slate-200/50 pb-1.5"><span className="text-slate-400">Bank</span><span className="font-semibold text-slate-700 dark:text-slate-200">{bankDetails.bank_name || 'N/A'}</span></div>
                <div className="flex justify-between border-b border-slate-200/50 pb-1.5"><span className="text-slate-400">Holder</span><span className="font-semibold text-slate-700 dark:text-slate-200">{bankDetails.account_holder_name || bankDetails.client_name || bankDetails.username || 'N/A'}</span></div>
                <div className="flex justify-between border-b border-slate-200/50 pb-1.5"><span className="text-slate-400">A/c No</span><span className="font-semibold text-slate-700 dark:text-slate-200">{bankDetails.bank_account_number || 'N/A'}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">IFSC</span><span className="font-semibold text-slate-700 dark:text-slate-200">{bankDetails.ifsc_code || 'N/A'}</span></div>
              </div>
            ) : (
              <div className="bg-slate-50 dark:bg-slate-800/40 rounded-xl p-3.5 space-y-2.5 text-xs text-slate-400">
                <div className="flex justify-between border-b border-slate-200/50 pb-1.5"><span>Bank</span><span className="font-medium text-slate-500">Not Saved</span></div>
                <div className="flex justify-between border-b border-slate-200/50 pb-1.5"><span>Holder</span><span className="font-medium text-slate-500">Not Saved</span></div>
                <div className="flex justify-between border-b border-slate-200/50 pb-1.5"><span>A/c No</span><span className="font-medium text-slate-500">Not Saved</span></div>
                <div className="flex justify-between"><span>IFSC</span><span className="font-medium text-slate-500">Not Saved</span></div>
              </div>
            )}
          </div>

          {/* Client Bank Detail */}
          <div className="bg-white dark:bg-[#111b21] rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                  <UserCircle size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Client Bank Detail</h3>
                  <p className="text-[11px] text-slate-400">Bank account details used for client transactions</p>
                </div>
              </div>
              {user?.role !== 'calling_handler' && (
                <button onClick={() => toast.success('Copied!')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-slate-500 transition-colors" title="Copy">
                  <Copy size={16} />
                </button>
              )}
            </div>

            {clientBankDetails && (clientBankDetails.bank_name || clientBankDetails.bank_account_number || clientBankDetails.ifsc_code) ? (
              <div className="bg-slate-50 dark:bg-slate-800/40 rounded-xl p-3.5 space-y-2.5 text-xs">
                <div className="flex justify-between border-b border-slate-200/50 pb-1.5"><span className="text-slate-400">Bank</span><span className="font-semibold text-slate-700 dark:text-slate-200">{clientBankDetails.bank_name || 'N/A'}</span></div>
                <div className="flex justify-between border-b border-slate-200/50 pb-1.5"><span className="text-slate-400">Holder</span><span className="font-semibold text-slate-700 dark:text-slate-200">{clientBankDetails.account_holder_name || 'N/A'}</span></div>
                <div className="flex justify-between border-b border-slate-200/50 pb-1.5"><span className="text-slate-400">A/c No</span><span className="font-semibold text-slate-700 dark:text-slate-200">{clientBankDetails.bank_account_number || 'N/A'}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">IFSC</span><span className="font-semibold text-slate-700 dark:text-slate-200">{clientBankDetails.ifsc_code || 'N/A'}</span></div>
              </div>
            ) : (
              <div className="bg-slate-50 dark:bg-slate-800/40 rounded-xl p-3.5 space-y-2.5 text-xs text-slate-400">
                <div className="flex justify-between border-b border-slate-200/50 pb-1.5"><span>Bank</span><span className="font-medium text-slate-500">Not Saved</span></div>
                <div className="flex justify-between border-b border-slate-200/50 pb-1.5"><span>Holder</span><span className="font-medium text-slate-500">Not Saved</span></div>
                <div className="flex justify-between border-b border-slate-200/50 pb-1.5"><span>A/c No</span><span className="font-medium text-slate-500">Not Saved</span></div>
                <div className="flex justify-between"><span>IFSC</span><span className="font-medium text-slate-500">Not Saved</span></div>
              </div>
            )}
          </div>
        </div>

        {/* 6. Actions Card */}
        {user?.role !== 'calling_handler' && (
          <div className="bg-white dark:bg-[#111b21] rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
                <Gear size={20} />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">Actions</h3>
                <p className="text-xs text-slate-400">Manage customer account</p>
              </div>
            </div>

            <div className={`grid ${isEmbedded ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'} gap-3`}>
              {(user?.role === 'super_admin' || permissions?.can_block_clients || user?.settings_powers?.blocked_clients !== false) && (
                !customer.is_blocked ? (
                  <button
                    onClick={() => setShowBlockConfirm(true)}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold rounded-xl shadow-md transition-all text-sm cursor-pointer"
                  >
                    <Prohibit size={18} weight="bold" />
                    Block Customer
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/customers/unblock/${customerId}`);
                        window.location.reload();
                      } catch (error) {
                        alert('Failed to unblock');
                      }
                    }}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-md transition-all text-sm cursor-pointer"
                  >
                    Unblock Customer
                  </button>
                )
              )}

              {(user?.role === 'super_admin' || user?.role === 'admin') && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-bold rounded-xl shadow-md transition-all text-sm"
                >
                  <Trash size={18} weight="bold" />
                  Delete Customer
                </button>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Block Confirmation Modal */}
      {showBlockConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-wa-panel rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-xl font-bold text-wa-text-primary mb-2">Block Customer?</h3>
            <p className="text-wa-text-secondary mb-6">
              This will block the customer's IP address. They won't be able to login or send messages.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBlockConfirm(false)}
                className="flex-1 px-4 py-2 border border-wa-border rounded-lg hover:bg-wa-hover"
              >
                Cancel
              </button>
              <button
                onClick={handleBlock}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
              >
                Block
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-wa-panel rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-xl font-bold text-wa-text-primary mb-2">Delete Customer?</h3>
            <p className="text-wa-text-secondary mb-6">
              This will permanently delete the customer and all their data. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-wa-border rounded-lg hover:bg-wa-hover"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Gallery Modal */}
      {showGallery && (
        <PhotoGallery
          photos={media.filter(m => m.image_url).map(m => m.image_url.startsWith('http') ? m.image_url : `${API}${m.image_url}`)}
          initialIndex={galleryStartIndex}
          onClose={() => setShowGallery(false)}
        />
      )}

      {/* Edit Login Modal */}
      {showEditLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-wa-panel rounded-lg w-full max-w-md">
            <div className="bg-wa-primary-strong text-white p-4 rounded-t-lg flex items-center justify-between">
              <h3 className="font-bold text-lg">Edit Login Details</h3>
              <button onClick={() => setShowEditLogin(false)} className="p-1 hover:bg-wa-primary-strong rounded-full">
                <ArrowLeft size={20} weight="bold" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-wa-text-primary mb-1">Username</label>
                <input
                  type="text"
                  value={customer.username || ''}
                  disabled
                  className="w-full px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-secondary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-wa-text-primary mb-1">DWS Login ID (Phone)</label>
                <input
                  type="text"
                  value={editLoginData.phone}
                  disabled
                  className="w-full px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-secondary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-wa-text-primary mb-1">DWS Login Password</label>
                <input
                  type="text"
                  value={editLoginData.password}
                  onChange={(e) => setEditLoginData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter new password"
                  className="w-full px-3 py-2 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-wa-text-primary mb-1">Login Link</label>
                <input
                  type="text"
                  value={settingsLoginLink || `${window.location.origin}/customer-login`}
                  disabled
                  className="w-full px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-secondary text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleResetPassword}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-semibold text-sm"
                >
                  Reset Default (Abcd1234)
                </button>
                <button
                  onClick={handleSaveLogin}
                  className="flex-1 px-4 py-2 bg-wa-primary-strong text-white rounded-lg hover:bg-green-700 font-semibold text-sm"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HandlerProfile;
